import { useState, type ReactNode } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  attemptLine,
  canRetryTruncation,
  initialParse,
  initialSchema,
  initialTruncation,
  initialValidate,
  lastReplyText,
  lastTruncationAttempt,
  linesText,
  loadsText,
  parseOutcome,
  PARSE_REPLY_KINDS,
  parseReplies,
  parseSummary,
  SCHEMA_NOTE_IDS,
  schemaNote,
  schemaOutcome,
  schemaReply,
  schemaRequest,
  schemaSummary,
  TASK_COUNTS,
  TITLE_KINDS,
  titleLabels,
  toParseReplyKind,
  toSchemaNoteId,
  toTaskCount,
  toTitleKind,
  toTruncationCeiling,
  TRUNCATION_ATTEMPT_CAP,
  TRUNCATION_CEILINGS,
  truncationSummary,
  truncationTransition,
  validateJson,
  validateResult,
  validateSummary,
  validationText,
  yesNo,
  type TruncationAction,
} from './output-models';

import './relationship-labs.css';
import './output-labs.css';

function Predict({ children }: { children: ReactNode }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="cb-check">
      <input type="checkbox" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}
const SIMULATION = 'A simulation built from real runs.';

export function OutputParseLab() {
  const [state, setState] = useState(initialParse);
  const outcome = parseOutcome(state);
  const summary = parseSummary(state);
  return (
    <Lab
      title="Which reply can our code store?"
      eyebrow="Output model · parsing"
      className="rel-lab output-lab"
    >
      <Predict>
        Which of the four replies leaves a title the code can store? Try each one under Reply.
      </Predict>
      <div className="cb-controls">
        <label>
          Reply
          <ModelSelect
            aria-label="Reply"
            value={state.reply}
            onValueChange={(value) =>
              setState((current) => ({ ...current, reply: toParseReplyKind(value) }))
            }
            options={PARSE_REPLY_KINDS.map((kind) => ({
              value: kind,
              label: parseReplies[kind].label,
            }))}
          />
        </label>
        <Reset onClick={() => setState(initialParse())} />
      </div>
      <Panel label="Reply">
        <pre aria-label="Reply">{outcome.text}</pre>
      </Panel>
      <div className="cb-two">
        <Panel label="splitlines()">
          <pre aria-label="Lines">{linesText(outcome.lines)}</pre>
        </Panel>
        <Panel label="json.loads()">
          <pre aria-label="Parsed">{loadsText(outcome.loads)}</pre>
        </Panel>
      </div>
      <Facts
        values={[
          ['Lines', String(outcome.lines.length)],
          ['Parses as JSON', yesNo(outcome.loads.ok)],
          ['Storable titles', String(outcome.titles.length)],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{SIMULATION}</p>
    </Lab>
  );
}

export function OutputValidateLab() {
  const [state, setState] = useState(initialValidate);
  const result = validateResult(state);
  const summary = validateSummary(state);
  return (
    <Lab
      title="Why is valid JSON not yet a valid suggestion?"
      eyebrow="Output model · validation"
      className="rel-lab output-lab"
    >
      <Predict>
        Will an empty title fail, and will 0 tasks fail? Set Title to Empty, then set Number of
        tasks to 0.
      </Predict>
      <div className="cb-controls">
        <label>
          Title
          <ModelSelect
            aria-label="Title"
            value={state.title}
            onValueChange={(value) =>
              setState((current) => ({ ...current, title: toTitleKind(value) }))
            }
            options={TITLE_KINDS.map((kind) => ({ value: kind, label: titleLabels[kind] }))}
          />
        </label>
        <label>
          Number of tasks
          <ModelSelect
            aria-label="Number of tasks"
            value={String(state.count)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, count: toTaskCount(value) }))
            }
            options={TASK_COUNTS.map((count) => ({ value: String(count), label: String(count) }))}
          />
        </label>
        <Reset onClick={() => setState(initialValidate())} />
      </div>
      <div className="cb-controls">
        <Toggle
          label="Add a field the model invented (priority)"
          checked={state.extraField}
          onChange={() => setState((current) => ({ ...current, extraField: !current.extraField }))}
        />
        <Toggle
          label="Title as a number"
          checked={state.numericTitle}
          onChange={() =>
            setState((current) => ({ ...current, numericTitle: !current.numericTitle }))
          }
        />
      </div>
      <div className="cb-two">
        <Panel label="JSON">
          <pre aria-label="JSON">{validateJson(state)}</pre>
        </Panel>
        <Panel label="Validation">
          <pre aria-label="Validation">{validationText(state)}</pre>
        </Panel>
      </div>
      <Facts
        values={[
          ['Parses as JSON', 'yes'],
          ['Validates', yesNo(result.ok)],
          ['Stored titles', String(result.ok ? result.titles.length : 0)],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{SIMULATION}</p>
    </Lab>
  );
}

export function OutputSchemaLab() {
  const [state, setState] = useState(initialSchema);
  const outcome = schemaOutcome(state);
  const summary = schemaSummary(state);
  return (
    <Lab
      title="What does schema enforcement guarantee?"
      eyebrow="Output model · the schema"
      className="rel-lab output-lab"
    >
      <Predict>
        Will the reply for note 3 parse without the schema? Set Note to Note 3, then turn on Send
        response_format with the schema.
      </Predict>
      <div className="cb-controls">
        <label>
          Note
          <ModelSelect
            aria-label="Note"
            value={String(state.note)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, note: toSchemaNoteId(value) }))
            }
            options={SCHEMA_NOTE_IDS.map((id) => ({
              value: String(id),
              label: `Note ${id}: ${schemaNote(id).title}`,
            }))}
          />
        </label>
        <Toggle
          label="Send response_format with the schema"
          checked={state.withSchema}
          onChange={() => setState((current) => ({ ...current, withSchema: !current.withSchema }))}
        />
        <Reset onClick={() => setState(initialSchema())} />
      </div>
      <div className="cb-two">
        <Panel label="Request">
          <pre aria-label="Request">{schemaRequest(state)}</pre>
        </Panel>
        <Panel label="Reply">
          <pre aria-label="Reply">{schemaReply(state)}</pre>
        </Panel>
      </div>
      <Facts
        values={[
          ['Parses', yesNo(outcome.parses)],
          ['Validates', yesNo(outcome.validates)],
          ['Correct', yesNo(outcome.correct)],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{SIMULATION}</p>
    </Lab>
  );
}

export function OutputTruncationLab() {
  const [state, setState] = useState(() => initialTruncation());
  const act = (action: TruncationAction) =>
    setState((current) => truncationTransition(current, action));
  const last = lastTruncationAttempt(state);
  const summary = truncationSummary(state);
  return (
    <Lab
      title="What happens to JSON when the reply is cut?"
      eyebrow="Output model · a cut reply"
      className="rel-lab output-lab"
    >
      <Predict>
        What does json.loads get from a reply cut at 20 tokens? Press Retry with twice the ceiling
        until the reply completes.
      </Predict>
      <div className="cb-controls">
        <label>
          max_tokens
          <ModelSelect
            aria-label="max_tokens"
            value={String(state.ceiling)}
            onValueChange={(value) => act({ type: 'ceiling', value: toTruncationCeiling(value) })}
            options={TRUNCATION_CEILINGS.map((size) => ({
              value: String(size),
              label: String(size),
            }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          disabled={!canRetryTruncation(state)}
          onClick={() => act({ type: 'retry' })}
        >
          Retry with twice the ceiling
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div className="cb-two">
        <Panel label="Attempts">
          <pre aria-label="Attempts">{state.attempts.map(attemptLine).join('\n')}</pre>
        </Panel>
        <Panel label="Last reply">
          <pre aria-label="Last reply">{lastReplyText(last)}</pre>
        </Panel>
      </div>
      <Facts
        values={[
          ['Attempts', `${state.attempts.length} of ${TRUNCATION_ATTEMPT_CAP}`],
          ['Ceiling now', String(last.maxTokens)],
          ['Parses', yesNo(last.loads.ok)],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{SIMULATION}</p>
    </Lab>
  );
}
